import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
    Avatar,
    Collapse,
    Descriptions,
    Drawer,
    FloatButton,
    Layout as AntLayout,
    List,
    Menu,
    Modal,
    Pagination,
    Spin,
    theme,
    Tooltip
} from 'antd';
import {useLocation, useNavigate} from 'react-router-dom';
import {versionService} from '../../services/versionService';
import type {VersionRecord} from '../../types/version';
import '../../styles/dark-theme.css';
import {
    ApiOutlined,
    ClockCircleOutlined,
    CloudDownloadOutlined,
    ClusterOutlined,
    DashboardOutlined,
    DatabaseOutlined,
    DeleteOutlined,
    DollarOutlined,
    GiftOutlined,
    GlobalOutlined,
    HistoryOutlined,
    LeftOutlined,
    MailOutlined,
    NodeIndexOutlined,
    NotificationOutlined,
    PlayCircleOutlined,
    ReadOutlined,
    RightOutlined,
    RobotOutlined,
    SafetyOutlined,
    SecurityScanOutlined,
    SettingOutlined,
    TagOutlined,
    TeamOutlined,
    ThunderboltOutlined,
    ToolOutlined,
    UpOutlined,
    UserOutlined,
} from '@ant-design/icons';
import FloatingToggleButton from './FloatingToggleButton';
import {useMenuMode} from '../../contexts/MenuModeContext';
import {useSidebarState} from '../../contexts/SidebarContext';
import {useAuth} from '../../contexts/AuthContext';
import ChatFloatWindow from '../chat/ChatFloatWindow';
import ChangePasswordForm from '../../pages/system/components/ChangePasswordForm';
import AnnouncementBanner from '../announcement/AnnouncementBanner';
import AnnouncementModal from '../announcement/AnnouncementModal';
import {Announcement} from '../../types/announcement';
// import TestChat from '../chat/TestChat';

const {Header, Sider, Content} = AntLayout;

/**
 * 满屏终端型页面路由前缀。
 * 这类页面自带完整画布（工具栏 / 图表 / 侧栏 / 状态栏），
 * 内容区需去掉内边距与圆角，改用 .layout-content--flush。
 * 见 styles/layout-shell.css。
 */
const FLUSH_ROUTES = ['/tools/pattern', '/tools/composite-signal', '/tools/research-report'];

interface LayoutProps {
    children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({children}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const isFlushPage = FLUSH_ROUTES.some(prefix => location.pathname.startsWith(prefix));
    const {collapsed, toggleCollapsed, setCollapsed} = useSidebarState();
    const {menuMode, setMenuMode} = useMenuMode();
    const {user, logout, hasPermission} = useAuth();
    const [userMenuExpanded, setUserMenuExpanded] = useState(false);
    const [hoveredMenuItem, setHoveredMenuItem] = useState<string | null>(null);
    /** 当前版本（用户菜单展示 + 更新说明弹窗）；未设置时该行不展示 */
    const [currentVersion, setCurrentVersion] = useState<VersionRecord | null>(null);
    const [versionModalOpen, setVersionModalOpen] = useState(false);
    const [openKeys, setOpenKeys] = useState<string[]>(getOpenKeys());
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    // 主流币种实时价格
    const [prices, setPrices] = useState<Record<string, { lastPrice: number; changePercent24H: number }>>({});
    const [priceLoading, setPriceLoading] = useState(true);
    const [countdown, setCountdown] = useState(60);
    // 消息相关状态
    const [messageCount, setMessageCount] = useState(0);
    const [messageModalOpen, setMessageModalOpen] = useState(false);
    const [messageList, setMessageList] = useState<any[]>([]);
    const [messageLoading, setMessageLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [expandedContent, setExpandedContent] = useState<string>('');
    const [messageDetailVisible, setMessageDetailVisible] = useState(false);

    // 公告相关状态
    const [announcementModalVisible, setAnnouncementModalVisible] = useState(false);
    const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);

    // 公告点击处理
    const handleAnnouncementClick = (announcement: Announcement) => {
        setSelectedAnnouncement(announcement);
        setAnnouncementModalVisible(true);
    };

    // 币种列表配置 (CoinGecko ID映射)
    const CRYPTO_LIST = [
        {symbol: 'BTC', id: 'bitcoin'},
        {symbol: 'ETH', id: 'ethereum'},
        {symbol: 'SOL', id: 'solana'},
        {symbol: 'SUI', id: 'sui'},
        {symbol: 'ADA', id: 'cardano'},
        {symbol: 'XRP', id: 'ripple'},
        {symbol: 'OKB', id: 'okb'},
        {symbol: 'BNB', id: 'binancecoin'},
    ];
    const {
        token: {colorBgContainer},
    } = theme.useToken();

    const handleLogout = () => {
        logout();
        window.location.href = '/login';
    };

    // 当前版本：仅拉一次，失败静默（版本展示不该影响页面可用性）
    useEffect(() => {
        let alive = true;
        versionService.getCurrent()
            .then(data => {
                if (alive) {
                    setCurrentVersion(data);
                }
            })
            .catch(() => {
            });
        return () => {
            alive = false;
        };
    }, []);

    // 监听collapsed状态，当侧边栏折叠时自动收起个人信息菜单
    useEffect(() => {
        if (collapsed) {
            setUserMenuExpanded(false);
        }
    }, [collapsed]);

    // 侧边栏展开时恢复当前路由对应的父级菜单，并定位到当前选中的菜单项
    useEffect(() => {
        if (collapsed) {
            return;
        }

        setOpenKeys(getOpenKeys());

        const timeout = window.setTimeout(() => {
            const menuEl = document.querySelector<HTMLDivElement>('.dark-menu');
            const selectedItem = menuEl?.querySelector<HTMLElement>('.ant-menu-item-selected');
            if (!menuEl || !selectedItem) {
                return;
            }

            const menuRect = menuEl.getBoundingClientRect();
            const itemRect = selectedItem.getBoundingClientRect();
            const scrollTop = menuEl.scrollTop + itemRect.top - menuRect.top - menuRect.height / 2 + itemRect.height / 2;
            menuEl.scrollTo({top: Math.max(0, scrollTop), behavior: 'smooth'});
        }, 250);

        return () => window.clearTimeout(timeout);
    }, [collapsed, location.pathname, menuMode]);

    // 监听路由变化，确保菜单模式与URL保持一致（解决浏览器前进/后退和刷新时的问题）
    React.useEffect(() => {
        if (location.pathname.startsWith('/system') || location.pathname.startsWith('/token-stats')
            || location.pathname.startsWith('/proxy') || location.pathname.startsWith('/data-fetch')
            || location.pathname.startsWith('/tasks') || location.pathname.startsWith('/executions')) {
            if (menuMode !== 'system') {
                setMenuMode('system');
            }
        } else {
            // 非系统管理路径，且当前处于系统管理模式时，切换回交易模式
            if (menuMode === 'system') {
                setMenuMode('trading');
            }
        }
        // 路径变化时同步更新菜单展开状态
        setOpenKeys(getOpenKeys());
    }, [location.pathname, menuMode, setMenuMode]);

    // 获取主流币种价格 (通过CoinGecko API)
    const fetchPrices = async () => {
        setPriceLoading(true);
        try {
            const ids = CRYPTO_LIST.map(c => c.id).join(',');
            const url = `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${ids}&order=market_cap_desc&sparkline=false&price_change_percentage=24h`;
            const response = await fetch(url);
            const data = await response.json();

            if (Array.isArray(data)) {
                const priceMap: Record<string, { lastPrice: number; changePercent24H: number }> = {};
                data.forEach((item: any) => {
                    const symbol = CRYPTO_LIST.find(c => c.id === item.id)?.symbol;
                    if (symbol) {
                        priceMap[symbol] = {
                            lastPrice: item.current_price,
                            changePercent24H: item.price_change_percentage_24h
                        };
                    }
                });
                setPrices(priceMap);
            }
        } catch (err: any) {
            // console.error('获取价格失败:', err);
        } finally {
            setPriceLoading(false);
            // 刷新完成后更新下次刷新时间
            nextRefreshTime.current = Date.now() + 60000;
        }
    };

    // 价格获取与倒计时同步 - 每次刷新后重置倒计时
    useEffect(() => {
        const tick = () => {
            fetchPrices();
        };

        // 立即获取一次价格
        tick();

        // 定时获取
        const interval = setInterval(tick, 60000);
        return () => clearInterval(interval);
    }, []);

    // 倒计时逻辑 - 简化为显示刷新间隔
    const nextRefreshTime = useRef<number>(Date.now() + 60000);

    useEffect(() => {
        const timer = setInterval(() => {
            const remaining = Math.max(0, Math.ceil((nextRefreshTime.current - Date.now()) / 1000));
            setCountdown(remaining);
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // 获取未读消息数量（30秒轮询）
    const fetchUnreadCount = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            if (!token) return;
            const response = await fetch('/api/v1/users/messages/unread-count', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) return;
            const data = await response.json();
            // 兼容字符串"0"和数字0
            if (data.code === 0 || data.code === '0') {
                setMessageCount(data.data);
            }
        } catch (err) {
            console.error('获取未读消息数量失败:', err);
        }
    };

    // 获取消息列表
    const fetchMessageList = async (page: number) => {
        setMessageLoading(true);
        try {
            const token = localStorage.getItem('auth_token');
            if (!token) return;
            const response = await fetch(`/api/v1/users/messages?page=${page}&size=20`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) return;
            const data = await response.json();
            if (data.code === 0 || data.code === '0') {
                setMessageList(data.data.content);
                setTotalPages(data.data.totalPages);
                setCurrentPage(page);
            }
        } catch (err) {
            console.error('获取消息列表失败:', err);
        } finally {
            setMessageLoading(false);
        }
    };

    // 点击消息获取详情并标记已读，弹窗展示
    const handleMessageClick = async (message: any) => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await fetch(`/api/v1/users/messages/${message.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            if (data.code === 0 || data.code === '0') {
                const content = data.data.content;
                // 检测是否为联系我消息跳转
                const contactMatch = content.match(/^\[联系我-(\d+)\]/);
                if (contactMatch) {
                    setMessageModalOpen(false);
                    const contactId = contactMatch[1];
                    navigate(`/system/contact-message-management?highlight=${contactId}`);
                    return;
                }
                setExpandedContent(content);
                setMessageDetailVisible(true);
                // 更新列表中的已读状态
                setMessageList(prev => prev.map((m: any) =>
                    m.id === message.id ? {...m, isRead: true} : m
                ));
                // 刷新未读数量
                fetchUnreadCount();
            }
        } catch (err) {
            console.error('获取消息详情失败:', err);
        }
    };

    // 删除消息
    const handleDeleteMessage = async (id: number) => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await fetch(`/api/v1/users/messages/${id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            if (data.code === 0 || data.code === '0') {
                // 刷新列表
                fetchMessageList(currentPage);
                // 刷新未读数量
                fetchUnreadCount();
            }
        } catch (err) {
            console.error('删除消息失败:', err);
        }
    };

    // 渲染风控通知展开详情
    const renderRiskNotification = (content: string) => {
        const lines = content.split('\n');
        const title = lines[0];
        const orderLabels = ['交易对', '方向', '数量', '价格', '杠杆'];
        const reportLabels = ['风控摘要', '风控建议', '推荐止盈价', '推荐止损价', '异常信息', '风险点'];

        const parseFields = (labels: string[]) => {
            return lines.slice(1)
                .map(line => {
                    const idx = line.indexOf(':');
                    if (idx === -1) return null;
                    return {label: line.substring(0, idx).trim(), value: line.substring(idx + 1).trim()};
                })
                .filter((item): item is { label: string; value: string } =>
                    item !== null && labels.some(l => item.label.includes(l)));
        };

        const orderItems = parseFields(orderLabels);
        const reportItems = parseFields(reportLabels);
        const descStyle = {
            label: {color: 'var(--pt-text-secondary)', width: '90px', fontSize: '13px'},
            content: {color: 'var(--pt-text-primary)', fontSize: '13px'}
        };

        return (
            <div>
                <div style={{fontSize: '15px', fontWeight: 600, color: '#F0B429', marginBottom: '8px'}}>{title}</div>
                {orderItems.length > 0 && (
                    <Descriptions column={1} size="small" bordered styles={descStyle}>
                        {orderItems.map((item, idx) => (
                            <Descriptions.Item key={idx} label={item.label}>{item.value}</Descriptions.Item>
                        ))}
                    </Descriptions>
                )}
                {reportItems.length > 0 && (
                    <Collapse defaultActiveKey={['report']} ghost size="small" style={{marginTop: '8px'}}
                              items={[{
                                  key: 'report',
                                  label: <span
                                      style={{color: '#8B5CF6', fontSize: '13px', fontWeight: 500}}>AI风控报告</span>,
                                  children: (
                                      <Descriptions column={1} size="small" bordered styles={descStyle}>
                                          {reportItems.map((item, idx) => (
                                              <Descriptions.Item key={idx}
                                                                 label={item.label}>{item.value}</Descriptions.Item>
                                          ))}
                                      </Descriptions>
                                  )
                              }]}
                    />
                )}
            </div>
        );
    };

    // 消息轮询（30秒）
    useEffect(() => {
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 30000);
        return () => clearInterval(interval);
    }, []);

    // 打开消息弹窗时加载消息列表
    useEffect(() => {
        if (messageModalOpen) {
            fetchMessageList(0);
        }
    }, [messageModalOpen]);

    // 交易模式菜单项 - 二级嵌套结构
    const tradingMenuItems = [
        // 概览（一级） -> 展开二级菜单
        {
            key: 'overview',
            icon: <DashboardOutlined/>,
            label: '概览',
            children: [
                {key: '/dashboard', label: '仪表板', permissionCode: 'PAGE_TRADING_DASHBOARD'},
                {key: '/positions', label: '合约持仓', permissionCode: 'PAGE_POSITIONS'},
            ],
        },
        // 交易中心（一级） -> 展开二级菜单
        {
            key: 'trade-center',
            icon: <DollarOutlined/>,
            label: '交易中心',
            children: [
                {key: '/trading', label: '辅助交易', permissionCode: 'PAGE_TRADING'},
                {key: '/hedge', label: '对冲管理', permissionCode: 'PAGE_TRADING'},
                {key: '/bot', label: 'BOT', permissionCode: 'PAGE_BOT'},
            ],
        },
        // 策略中心（一级） -> 展开二级菜单
        {
            key: 'strategy-center',
            icon: <ThunderboltOutlined/>,
            label: '策略中心',
            children: [
                {key: '/strategy/list', label: '我的策略', permissionCode: 'PAGE_STRATEGY_LIST'},
                {key: '/strategy/tasks', label: '策略任务', permissionCode: 'PAGE_STRATEGY_TASKS'},
                {key: '/strategy/backtest', label: '回测任务', permissionCode: 'PAGE_BACKTEST'},
            ],
        },
        // 学习中心（一级） -> 展开二级菜单
        {
            key: 'learning-center',
            icon: <TeamOutlined/>,
            label: '学习中心',
            children: [
                {key: '/strategy/logic-flow', label: '策略编辑器', permissionCode: 'PAGE_LOGIC_FLOW'},
                {key: '/learning/simulate', label: '模拟交易', permissionCode: 'PAGE_SIMULATE'},
                {key: '/ai-backtest', label: 'AI回测', permissionCode: 'PAGE_AI_BACKTEST'},
                // {
                //     key: '/learning/ai-strategy',
                //     icon: <BulbOutlined/>,
                //     label: 'AI策略学习',
                //     permissionCode: 'PAGE_AI_STRATEGY'
                // },
            ],
        },
        // 工具（一级） -> 展开二级菜单
        {
            key: 'tools',
            icon: <ToolOutlined/>,
            label: '工具',
            children: [
                {key: '/tools/radar', label: '市场雷达', permissionCode: 'PAGE_RADAR'},
                {key: '/tools/pattern', label: 'K线形态', permissionCode: 'PAGE_PATTERN'},
                {key: '/tools/composite-signal', label: '组合信号', permissionCode: 'PAGE_COMPOSITE_SIGNAL'},
                {key: '/tools/dca-assistant', label: 'DCA助手', permissionCode: 'PAGE_DCA_ASSISTANT'},
                {key: '/tools/research-report', label: '研报', permissionCode: 'PAGE_RESEARCH_REPORT'},
                {key: '/tools/news', label: '财经要闻', permissionCode: 'PAGE_NEWS'},
                {key: '/tools/square', label: '策略广场', permissionCode: 'PAGE_STRATEGY_SQUARE'},
            ],
        },
    ];

    // 系统管理模式菜单项 - 二级嵌套结构
    const systemMenuItems = [
        // 概览分组
        {
            key: 'overview',
            icon: <DashboardOutlined/>,
            label: '概览',
            children: [
                {key: '/system', label: 'Dashboard', permissionCode: 'SYSTEM_DASHBOARD'},
                {key: '/token-stats', label: '模型Token', permissionCode: 'PAGE_TOKEN_STATS'},
            ],
        },
        // 系统设置分组
        {
            key: 'system-settings-group',
            icon: <ToolOutlined/>,
            label: '系统设置',
            children: [
                {
                    key: '/system/exchange-data-settings',
                    icon: <CloudDownloadOutlined/>,
                    label: '数据采集配置',
                    permissionCode: 'SYSTEM_DATA_FETCH',
                },
                {
                    key: '/system/simulate-settings',
                    icon: <PlayCircleOutlined/>,
                    label: '模拟交易设置',
                    permissionCode: 'PAGE_SIMULATE_SETTINGS',
                },
                {
                    key: '/proxy',
                    icon: <GlobalOutlined/>,
                    label: '代理服务器',
                    permissionCode: 'SYSTEM_PROXY',
                },
                {
                    key: '/system/proxy-rules',
                    icon: <SafetyOutlined/>,
                    label: '代理规则',
                    permissionCode: 'PAGE_PROXY_RULES',
                },
                {
                    key: '/system/poc-display-settings',
                    icon: <SettingOutlined/>,
                    label: 'POC展示配置',
                },
            ],
        },
        // 运营配置分组
        {
            key: 'operation-config-group',
            icon: <NotificationOutlined/>,
            label: '运营配置',
            children: [
                {
                    key: '/system/operations-settings',
                    icon: <SettingOutlined/>,
                    label: '报告品种配置',
                    permissionCode: 'PAGE_OPERATIONS_CONFIG',
                },
                {
                    key: '/system/announcement-management',
                    icon: <NotificationOutlined/>,
                    label: '系统公告',
                    permissionCode: 'PAGE_ANNOUNCEMENT_MGMT',
                },
                {
                    key: '/system/version-management',
                    icon: <TagOutlined/>,
                    label: '版本管理',
                    permissionCode: 'PAGE_VERSION_MGMT',
                },
                {
                    key: '/system/news-management',
                    icon: <ReadOutlined/>,
                    label: '财经要闻',
                    permissionCode: 'PAGE_NEWS_MGMT',
                },
                {
                    key: '/system/waitlist-management',
                    icon: <TeamOutlined/>,
                    label: '等待列表',
                },
                {
                    key: '/system/contact-message-management',
                    icon: <MailOutlined/>,
                    label: '联系我',
                },
            ],
        },
        // 权限管理分组
        {
            key: 'auth-group',
            icon: <SafetyOutlined/>,
            label: '权限管理',
            children: [
                {
                    key: '/system/user-management',
                    icon: <UserOutlined/>,
                    label: '用户管理',
                    permissionCode: 'SYSTEM_USER_MANAGEMENT',
                },
                {
                    key: '/system/role-management',
                    icon: <TeamOutlined/>,
                    label: '角色管理',
                    permissionCode: 'SYSTEM_ROLE_MANAGEMENT',
                },
                {
                    key: '/system/permission-management',
                    icon: <SafetyOutlined/>,
                    label: '权限管理',
                    permissionCode: 'SYSTEM_PERMISSION_MANAGEMENT',
                },
                {
                    key: '/system/invitation-code-management',
                    icon: <GiftOutlined/>,
                    label: '邀请码管理',
                    permissionCode: 'PAGE_INVITATION_CODE',
                },
            ],
        },
        // AI配置分组
        {
            key: 'ai-group',
            icon: <SecurityScanOutlined/>,
            label: 'AI配置',
            children: [
                {
                    key: '/system/ai-model-configs',
                    icon: <ClusterOutlined/>,
                    label: '大模型配置',
                    permissionCode: 'SYSTEM_AI_MODEL_CONFIGS',
                },
                {
                    key: '/system/skill-configs',
                    icon: <ThunderboltOutlined/>,
                    label: '技能配置管理',
                    permissionCode: 'SYSTEM_SKILL_CONFIGS',
                },
                {
                    key: '/system/memory-blocks',
                    icon: <TagOutlined/>,
                    label: '共享记忆块管理',
                    permissionCode: 'SYSTEM_AGENT_CONFIGS',
                },
                {
                    key: '/system/memory-pool',
                    icon: <DatabaseOutlined/>,
                    label: '记忆池管理',
                    permissionCode: 'SYSTEM_AGENT_CONFIGS',
                },
                {
                    key: '/system/agent-configs',
                    icon: <RobotOutlined/>,
                    label: '智能体配置',
                    permissionCode: 'SYSTEM_AGENT_CONFIGS',
                },
                {
                    key: '/system/agent-output-validations',
                    icon: <SecurityScanOutlined/>,
                    label: '产出校验记录',
                    permissionCode: 'SYSTEM_AGENT_CONFIGS',
                },
                {
                    key: '/system/agent-routing-configs',
                    icon: <NodeIndexOutlined/>,
                    label: '智能体路由配置',
                    permissionCode: 'SYSTEM_AGENT_CONFIGS',
                },
                {
                    key: '/system/workflow-bindings',
                    icon: <RobotOutlined/>,
                    label: '工作流绑定',
                    permissionCode: 'SYSTEM_WORKFLOW_BINDINGS',
                },
            ],
        },
        // Freqtrade分组
        {
            key: 'freqtrade-group',
            icon: <SettingOutlined/>,
            label: '量化配置',
            children: [
                {
                    key: '/system/freqtrade-config',
                    icon: <SettingOutlined/>,
                    label: 'Freqtrade配置',
                    permissionCode: 'SYSTEM_FREQTRADE_CONFIG',
                },
                {
                    key: '/system/strategy-config',
                    icon: <SettingOutlined/>,
                    label: '策略配置',
                    permissionCode: 'SYSTEM_STRATEGY_CONFIG',
                },
                {
                    key: '/system/strategy-execution',
                    icon: <PlayCircleOutlined/>,
                    label: '策略执行',
                    permissionCode: 'SYSTEM_STRATEGY_EXECUTION',
                },
                {
                    key: '/system/backtest',
                    icon: <HistoryOutlined/>,
                    label: '策略回测',
                    permissionCode: 'SYSTEM_BACKTEST',
                },
            ],
        },
        // 任务管理分组
        {
            key: 'task-group',
            icon: <ClockCircleOutlined/>,
            label: '任务管理',
            children: [
                {
                    key: '/data-fetch',
                    icon: <ApiOutlined/>,
                    label: '数据获取配置',
                    permissionCode: 'SYSTEM_DATA_FETCH_TASK',
                },
                {
                    key: '/tasks',
                    icon: <ClockCircleOutlined/>,
                    label: '定时任务管理',
                    permissionCode: 'SYSTEM_TASKS',
                },
                {
                    key: '/executions',
                    icon: <HistoryOutlined/>,
                    label: '执行记录',
                    permissionCode: 'SYSTEM_EXECUTIONS',
                },
            ],
        },
    ];

    // 递归过滤菜单项：无权限的子菜单不展示，所有子菜单都无权限时父菜单也不展示
    const filterMenuItems = (items: any[]): any[] => {
        return items.reduce((acc: any[], item: any) => {
            // 如果有children，递归过滤children
            if (item.children) {
                const filteredChildren = filterMenuItems(item.children);
                if (filteredChildren.length > 0) {
                    acc.push({...item, children: filteredChildren});
                }
                return acc;
            }
            // 叶子节点：检查permissionCode，没有则放行（兼容没有配置权限码的菜单）
            // 交易模式使用严格权限检查（ADMIN不豁免），系统管理模式使用宽松检查（ADMIN豁免）
            if (!item.permissionCode || hasPermission(item.permissionCode, menuMode === 'trading')) {
                acc.push(item);
            }
            return acc;
        }, []);
    };

    // 根据菜单模式获取并过滤菜单项
    const rawMenuItems = useMemo(() => {
        const raw = menuMode === 'trading' ? tradingMenuItems : systemMenuItems;
        return filterMenuItems(raw);
    }, [menuMode, hasPermission]);

    // 清除菜单项中的自定义属性（如permissionCode），防止Ant Design Menu警告
    const cleanMenuItem = (item: any): any => {
        const {permissionCode, ...rest} = item;
        if (rest.children) {
            return {
                ...rest,
                children: rest.children.map(cleanMenuItem)
            };
        }
        return rest;
    };

    const menuItems = useMemo(() => {
        return rawMenuItems.map(cleanMenuItem);
    }, [rawMenuItems]);

    // 获取当前选中的菜单项
    function getSelectedKeys() {
        const path = location.pathname;

        if (path === '/' || path === '/dashboard') {
            return ['/dashboard'];
        }
        if (path.startsWith('/system/')) {
            return [path];
        }
        if (path.startsWith('/positions')) {
            return ['/positions'];
        }
        if (path.startsWith('/trading') || path.startsWith('/bot')) {
            return [path];
        }
        // 策略中心
        if (path.startsWith('/strategy')) {
            return [path];
        }
        return [path];
    }

    // 获取展开的菜单项
    function getOpenKeys() {
        const path = location.pathname;

        if (path === '/' || path === '/dashboard' || path.startsWith('/positions') || path.startsWith('/token-stats')) {
            return ['overview'];
        }
        if (path.startsWith('/trading') || path.startsWith('/bot') || path.startsWith('/hedge')) {
            return ['trade-center'];
        }
        // 策略编辑器虽然以 /strategy 开头，但菜单中属于学习中心，需优先判断
        if (path.startsWith('/strategy/logic-flow')) {
            return ['learning-center'];
        }
        if (path.startsWith('/strategy')) {
            return ['strategy-center'];
        }
        if (path.startsWith('/tools')) {
            return ['tools'];
        }
        // 学习中心
        if (path.startsWith('/learning') || path.startsWith('/ai-backtest')) {
            return ['learning-center'];
        }
        // 已从 /system/ 迁出的独立路径，展开对应分组
        if (path.startsWith('/proxy') || path.startsWith('/data-fetch')
            || path.startsWith('/tasks') || path.startsWith('/executions')) {
            if (path.startsWith('/proxy')) {
                return ['system-settings-group'];
            }
            return ['task-group'];
        }
        if (path.startsWith('/system')) {
            if (path === '/system') {
                return ['overview'];
            }
            if (path.startsWith('/system/exchange') || path.startsWith('/system/simulate')
                || path.startsWith('/system/proxy')) {
                return ['system-settings-group'];
            }
            if (path.startsWith('/system/announcement') || path.startsWith('/system/news')
                || path.startsWith('/system/waitlist') || path.startsWith('/system/contact-message')) {
                return ['operation-config-group'];
            }
            if (path.startsWith('/system/user') || path.startsWith('/system/role')
                || path.startsWith('/system/permission') || path.startsWith('/system/invitation')) {
                return ['auth-group'];
            }
            if (path.startsWith('/system/ai') || path.startsWith('/system/skill')
                || path.startsWith('/system/agent') || path.startsWith('/system/workflow')
                || path.startsWith('/system/memory')) {
                return ['ai-group'];
            }
            if (path.startsWith('/system/freqtrade') || path.startsWith('/system/strategy')
                || path.startsWith('/system/backtest')) {
                return ['freqtrade-group'];
            }
            if (path.startsWith('/system/data') || path.startsWith('/system/task')
                || path.startsWith('/system/exec')) {
                return ['task-group'];
            }
        }
        return [];
    }

    // 生成面包屑
    const generateBreadcrumb = () => {
        const pathSnippets = location.pathname.split('/').filter((i) => i);
        const breadcrumbItems = [
            {
                title: '首页',
            },
        ];

        if (pathSnippets.length > 0) {
            if (pathSnippets[0] === 'system') {
                breadcrumbItems.push({
                    title: 'Dashboard',
                });

                if (pathSnippets.length > 1) {
                    const subPath = pathSnippets[1];
                    const subItem = systemMenuItems.find(item => item.key === `/system/${subPath}`);

                    if (subItem) {
                        breadcrumbItems.push({
                            title: subItem.label,
                        });
                    }
                }
            } else if (pathSnippets[0] === 'positions') {
                breadcrumbItems.push({
                    title: '合约持仓',
                });
            } else if (pathSnippets[0] === 'trading') {
                breadcrumbItems.push({
                    title: '永续合约交易',
                });
            } else if (pathSnippets[0] === 'bot') {
                breadcrumbItems.push({
                    title: '智能交易机器人',
                });
            }
        }

        return breadcrumbItems;
    };

    return (
        <AntLayout style={{minHeight: '100vh'}}>
            <Sider
                theme="dark"
                breakpoint="lg"
                collapsedWidth="60"
                collapsed={collapsed}
                onBreakpoint={(broken) => {
                    // console.log(broken);
                    // 当屏幕尺寸变化导致自动折叠时，同步更新我们的状态
                    if (broken !== collapsed) {
                        setCollapsed(broken);
                    }
                }}
                collapsible={false}  // 禁用Antd内置的折叠功能，完全由我们控制
                width={240}
                style={{
                    background: 'var(--pt-bg-panel)',
                    borderRight: 'none',
                    position: 'relative' // 为把手提供定位上下文
                }}
            >
                {/* 侧边栏控制把手 - 位于侧边栏右侧边缘 */}
                <div
                    onClick={toggleCollapsed}
                    className={collapsed ? 'sider-handle sider-handle--breathing' : 'sider-handle'}
                    style={{
                        position: 'fixed',
                        top: '50%',
                        left: collapsed ? 60 : 240,
                        width: '16px',
                        height: '60px',
                        transform: 'translateY(-50%)',
                        background: 'var(--pt-bg-panel)',
                        borderRadius: '0 8px 8px 0',
                        border: collapsed
                            ? '1px solid rgba(255, 255, 255, 0.01)'
                            : '1px solid rgba(255, 255, 255, 0.01)',
                        borderLeft: 'none',
                        cursor: 'pointer',
                        zIndex: 999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '4px 0 8px rgba(0,0,0,0.15)',
                        transition: 'background 0.3s, left 0.2s ease, border-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--pt-bg-hover)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'var(--pt-bg-panel)'}
                    title={collapsed ? "展开导航" : "收起导航"}
                >
                    <div style={{
                        color: 'var(--pt-text-secondary)',
                        fontSize: '10px',
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        {collapsed ? <RightOutlined style={{fontSize: '10px'}}/> :
                            <LeftOutlined style={{fontSize: '10px'}}/>}
                    </div>
                </div>

                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: collapsed ? 96 : 240,
                    height: 80,
                    background: collapsed ? 'transparent' : 'var(--pt-bg-panel)',
                    zIndex: collapsed ? 999 : 99,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'width 0.2s ease, background 0.2s ease, z-index 0.2s ease',
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        borderRadius: collapsed ? '50%' : '0',
                        overflow: 'hidden',
                        transition: 'border-radius 0.2s ease',
                    }}>
                        <img
                            src="/d-sense.png"
                            alt="D-Sense"
                            style={{
                                height: collapsed ? 80 : 56,
                                width: collapsed ? 80 : 56,
                                objectFit: 'contain',
                                borderRadius: collapsed ? '50%' : '0',
                                transition: 'height 0.2s ease, width 0.2s ease, border-radius 0.2s ease',
                            }}
                        />
                        {!collapsed && (
                            <span style={{
                                fontFamily: "'Syne', sans-serif",
                                fontWeight: 800,
                                display: 'inline-flex',
                                alignItems: 'baseline'
                            }}><span
                                style={{fontSize: '2.25rem', color: '#FFFFFF', marginRight: '0.25rem'}}>D</span><span
                                style={{fontSize: '1.5rem', opacity: 0.5}}></span><span
                                style={{fontSize: '1.5rem', color: 'var(--pt-primary)'}}>Sense</span></span>
                        )}
                    </div>
                </div>

                <Menu
                    theme="dark"
                    mode="inline"
                    inlineCollapsed={collapsed}
                    selectedKeys={getSelectedKeys()}
                    openKeys={collapsed ? [] : openKeys}
                    onOpenChange={(keys) => {
                        if (!collapsed) {
                            setOpenKeys(keys);
                        }
                    }}
                    items={menuItems}
                    onClick={({key}) => navigate(key)}
                    style={{
                        background: 'var(--pt-bg-panel)',
                        borderRight: 'none',
                        marginTop: 80,
                        overflowY: 'auto',
                        maxHeight: 'calc(100vh - 160px)',
                    }}
                    className="dark-menu"
                />

                {!collapsed && (
                    <div
                        style={{
                            position: 'fixed',
                            bottom: 0,
                            left: 0,
                            width: 240,
                            background: 'var(--pt-bg-panel)',
                            zIndex: 100,
                        }}
                        onMouseLeave={() => setUserMenuExpanded(false)}
                    >
                        {userMenuExpanded && (
                            <div style={{
                                background: 'var(--pt-bg-hover)',
                                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: '8px 8px 0 0',
                            }}>
                                <div
                                    style={{
                                        padding: '12px 16px',
                                        cursor: 'pointer',
                                        color: 'var(--pt-text-primary)',
                                        background: hoveredMenuItem === 'membership' ? 'var(--pt-bg-hover)' : 'transparent',
                                    }}
                                    onClick={() => {
                                        navigate('/membership');
                                        setUserMenuExpanded(false);
                                    }}
                                    onMouseEnter={() => setHoveredMenuItem('membership')}
                                    onMouseLeave={() => setHoveredMenuItem(null)}
                                >
                                    会员中心
                                </div>
                                {hasPermission('SYSTEM_AI_TRADING') && (
                                    <div
                                        style={{
                                            padding: '12px 16px',
                                            cursor: 'pointer',
                                            color: 'var(--pt-text-primary)',
                                            background: hoveredMenuItem === 'user-ai-config' ? 'var(--pt-bg-hover)' : 'transparent',
                                        }}
                                        onClick={() => {
                                            navigate('/user-ai-config');
                                            setUserMenuExpanded(false);
                                        }}
                                        onMouseEnter={() => setHoveredMenuItem('user-ai-config')}
                                        onMouseLeave={() => setHoveredMenuItem(null)}
                                    >
                                        AI交易配置
                                    </div>
                                )}
                                {hasPermission('PAGE_API_CONNECTION') && (
                                    <div
                                        style={{
                                            padding: '12px 16px',
                                            cursor: 'pointer',
                                            color: 'var(--pt-text-primary)',
                                            background: hoveredMenuItem === 'api-connection' ? 'var(--pt-bg-hover)' : 'transparent',
                                        }}
                                        onClick={() => {
                                            navigate('/api-connection');
                                            setUserMenuExpanded(false);
                                        }}
                                        onMouseEnter={() => setHoveredMenuItem('api-connection')}
                                        onMouseLeave={() => setHoveredMenuItem(null)}
                                    >
                                        API连接
                                    </div>
                                )}
                                <div
                                    style={{
                                        padding: '12px 16px',
                                        cursor: 'pointer',
                                        color: 'var(--pt-text-primary)',
                                        background: hoveredMenuItem === 'password' ? 'var(--pt-bg-hover)' : 'transparent',
                                    }}
                                    onClick={() => {
                                        setPasswordModalOpen(true);
                                        setUserMenuExpanded(false);
                                    }}
                                    onMouseEnter={() => setHoveredMenuItem('password')}
                                    onMouseLeave={() => setHoveredMenuItem(null)}
                                >
                                    修改密码
                                </div>
                                {null != currentVersion && (
                                    <div
                                        style={{
                                            padding: '12px 16px',
                                            cursor: 'pointer',
                                            color: 'var(--pt-text-primary)',
                                            background: hoveredMenuItem === 'version' ? 'var(--pt-bg-hover)' : 'transparent',
                                        }}
                                        title="点击查看更新说明"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setVersionModalOpen(true);
                                        }}
                                        onMouseEnter={() => setHoveredMenuItem('version')}
                                        onMouseLeave={() => setHoveredMenuItem(null)}
                                    >
                                        版本 v{currentVersion.version}
                                        {null != currentVersion.releaseDate && (
                                            <span style={{color: 'var(--pt-text-dim)', marginLeft: 6}}>
                                                · {String(currentVersion.releaseDate).slice(5)}
                                            </span>
                                        )}
                                    </div>
                                )}
                                <div
                                    style={{
                                        padding: '12px 16px',
                                        cursor: 'pointer',
                                        color: 'var(--pt-bear)',
                                        background: hoveredMenuItem === 'logout' ? 'var(--pt-bg-hover)' : 'transparent',
                                    }}
                                    onClick={handleLogout}
                                    onMouseEnter={() => setHoveredMenuItem('logout')}
                                    onMouseLeave={() => setHoveredMenuItem(null)}
                                >
                                    退出登录
                                </div>
                            </div>
                        )}

                        <div
                            style={{
                                padding: '16px',
                                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                            }}
                            onClick={() => setUserMenuExpanded(!userMenuExpanded)}
                        >
                            <Avatar
                                size={36}
                                style={{backgroundColor: 'var(--pt-primary)', flexShrink: 0}}
                            >
                                {user?.username?.charAt(0).toUpperCase() || 'U'}
                            </Avatar>
                            <span style={{
                                flex: 1,
                                color: 'var(--pt-text-primary)',
                                fontSize: '14px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}>
                                {user?.username || '用户'}
                            </span>
                            <UpOutlined
                                rotate={userMenuExpanded ? 180 : 0}
                                style={{color: 'var(--pt-text-secondary)', transition: 'transform 0.2s'}}
                            />
                        </div>
                    </div>
                )}

                {collapsed && (
                    <div
                        style={{
                            position: 'fixed',
                            bottom: 0,
                            left: 0,
                            width: 96,
                            background: 'transparent',
                            zIndex: 100,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            cursor: 'pointer',
                            transition: 'background 0.2s ease, border-top 0.2s ease',
                        }}
                        onMouseEnter={() => {
                            setUserMenuExpanded(true);
                        }}
                        onMouseLeave={() => setUserMenuExpanded(false)}
                    >
                        {userMenuExpanded && (
                            <div style={{
                                position: 'fixed',
                                bottom: 56,
                                left: 8,
                                background: 'var(--pt-bg-panel)',
                                borderRadius: '8px',
                                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.12)',
                                overflow: 'hidden',
                                zIndex: 105,
                            }}>
                                {hasPermission('SYSTEM_AI_TRADING') && (
                                    <div
                                        style={{
                                            padding: '12px 16px',
                                            cursor: 'pointer',
                                            color: 'var(--pt-text-primary)',
                                            background: hoveredMenuItem === 'user-ai-config' ? 'var(--pt-bg-hover)' : 'transparent',
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate('/user-ai-config');
                                            setUserMenuExpanded(false);
                                        }}
                                        onMouseEnter={() => setHoveredMenuItem('user-ai-config')}
                                        onMouseLeave={() => setHoveredMenuItem(null)}
                                    >
                                        AI交易配置
                                    </div>
                                )}
                                {hasPermission('PAGE_API_CONNECTION') && (
                                    <div
                                        style={{
                                            padding: '12px 16px',
                                            cursor: 'pointer',
                                            color: 'var(--pt-text-primary)',
                                            background: hoveredMenuItem === 'api-connection' ? 'var(--pt-bg-hover)' : 'transparent',
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate('/api-connection');
                                            setUserMenuExpanded(false);
                                        }}
                                        onMouseEnter={() => setHoveredMenuItem('api-connection')}
                                        onMouseLeave={() => setHoveredMenuItem(null)}
                                    >
                                        API连接
                                    </div>
                                )}
                                <div
                                    style={{
                                        padding: '12px 16px',
                                        cursor: 'pointer',
                                        color: 'var(--pt-text-primary)',
                                        background: hoveredMenuItem === 'password' ? 'var(--pt-bg-hover)' : 'transparent',
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setPasswordModalOpen(true);
                                        setUserMenuExpanded(false);
                                    }}
                                    onMouseEnter={() => setHoveredMenuItem('password')}
                                    onMouseLeave={() => setHoveredMenuItem(null)}
                                >
                                    修改密码
                                </div>
                                {null != currentVersion && (
                                    <div
                                        style={{
                                            padding: '12px 16px',
                                            cursor: 'pointer',
                                            color: 'var(--pt-text-primary)',
                                            background: hoveredMenuItem === 'version' ? 'var(--pt-bg-hover)' : 'transparent',
                                        }}
                                        title="点击查看更新说明"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setVersionModalOpen(true);
                                        }}
                                        onMouseEnter={() => setHoveredMenuItem('version')}
                                        onMouseLeave={() => setHoveredMenuItem(null)}
                                    >
                                        版本 v{currentVersion.version}
                                        {null != currentVersion.releaseDate && (
                                            <span style={{color: 'var(--pt-text-dim)', marginLeft: 6}}>
                                                · {String(currentVersion.releaseDate).slice(5)}
                                            </span>
                                        )}
                                    </div>
                                )}
                                <div
                                    style={{
                                        padding: '12px 16px',
                                        cursor: 'pointer',
                                        color: 'var(--pt-bear)',
                                        background: hoveredMenuItem === 'logout' ? 'var(--pt-bg-hover)' : 'transparent',
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleLogout();
                                        setUserMenuExpanded(false);
                                    }}
                                    onMouseEnter={() => setHoveredMenuItem('logout')}
                                    onMouseLeave={() => setHoveredMenuItem(null)}
                                >
                                    退出登录
                                </div>
                            </div>
                        )}

                        <div style={{
                            padding: '12px 0',
                        }}>
                            <Avatar
                                size={32}
                                style={{backgroundColor: 'var(--pt-primary)', flexShrink: 0}}
                            >
                                {user?.username?.charAt(0).toUpperCase() || 'U'}
                            </Avatar>
                        </div>
                    </div>
                )}
            </Sider>
            <AntLayout>
                {/* Header已移除，折叠按钮已移到侧边栏顶部 */}
                {/*
                <Header style={{
                    padding: '0 24px',
                    background: colorBgContainer,
                    borderBottom: '1px solid var(--pt-border-subtle)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <Button
                            type="text"
                            icon={collapsed ? <MenuUnfoldOutlined/> : <MenuFoldOutlined/>}
                            onClick={toggleCollapsed}
                            style={{
                                fontSize: '16px',
                                width: 40,
                                height: 40,
                            }}
                        />
                    </div>
                    <div>
                        { / * 这里可以添加用户信息或其他头部组件 * / }
                    </div>
                </Header>
                */}
                <Content style={{margin: 0}}>
                    {/* 价格Banner - 仅交易系统界面显示，固定顶端 */}
                    {menuMode === 'trading' && (
                        <div className="price-banner" onDoubleClick={() => fetchPrices()}>
                            <div className="price-banner-content" style={{
                                display: 'flex',
                                gap: '16px',
                                width: 'max-content',
                                animation: 'banner-scroll 30s linear infinite',
                            }}>
                                {CRYPTO_LIST.map((crypto) => {
                                    const price = prices[crypto.symbol];
                                    const iconFile = `/coins/${crypto.symbol.toLowerCase()}.webp`;
                                    const isLoading = priceLoading && !price;

                                    if (!price && !priceLoading) {
                                        // 无数据且非加载中，不渲染该元素
                                        return (
                                            <div key={crypto.symbol}
                                                 style={{
                                                     display: 'flex',
                                                     alignItems: 'center',
                                                     gap: '8px',
                                                     flexShrink: 0
                                                 }}>
                                                <img
                                                    src={iconFile}
                                                    alt={crypto.symbol}
                                                    style={{
                                                        width: 20,
                                                        height: 20,
                                                        borderRadius: '50%',
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                                <span style={{
                                                    fontSize: '14px',
                                                    fontFamily: 'IBM Plex Mono, monospace',
                                                    color: 'var(--pt-text-secondary)'
                                                }}>--</span>
                                                <span style={{
                                                    fontSize: '12px',
                                                    fontFamily: 'IBM Plex Mono, monospace',
                                                    color: 'var(--pt-text-secondary)',
                                                    padding: '2px 6px',
                                                    borderRadius: '4px',
                                                    background: 'rgba(136,146,164,0.15)'
                                                }}>--</span>
                                            </div>
                                        );
                                    }

                                    if (isLoading || !price) {
                                        // 加载中显示占位
                                        return (
                                            <div key={crypto.symbol}
                                                 style={{
                                                     display: 'flex',
                                                     alignItems: 'center',
                                                     gap: '8px',
                                                     flexShrink: 0
                                                 }}>
                                                <img
                                                    src={iconFile}
                                                    alt={crypto.symbol}
                                                    style={{
                                                        width: 20,
                                                        height: 20,
                                                        borderRadius: '50%',
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                                <span style={{
                                                    fontSize: '14px',
                                                    fontFamily: 'IBM Plex Mono, monospace',
                                                    color: 'var(--pt-text-secondary)'
                                                }}>加载中</span>
                                                <span style={{
                                                    fontSize: '12px',
                                                    fontFamily: 'IBM Plex Mono, monospace',
                                                    color: 'var(--pt-text-secondary)',
                                                    padding: '2px 6px',
                                                    borderRadius: '4px',
                                                    background: 'rgba(136,146,164,0.15)'
                                                }}>--</span>
                                            </div>
                                        );
                                    }

                                    const change = Number(price.changePercent24H || 0);
                                    return (
                                        <div key={crypto.symbol}
                                             style={{display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0}}
                                             className="price-item">
                                            <img
                                                src={iconFile}
                                                alt={crypto.symbol}
                                                style={{width: 20, height: 20, borderRadius: '50%', objectFit: 'cover'}}
                                            />
                                            <span style={{
                                                fontSize: '14px',
                                                fontFamily: 'IBM Plex Mono, monospace',
                                                color: 'var(--pt-text-primary)'
                                            }}>${Number(price.lastPrice || 0).toLocaleString()}</span>
                                            <span style={{
                                                fontSize: '12px',
                                                fontFamily: 'IBM Plex Mono, monospace',
                                                color: change >= 0 ? 'var(--pt-bull)' : 'var(--pt-bear)',
                                                padding: '2px 6px',
                                                borderRadius: '4px',
                                                background: change >= 0 ? 'rgba(0,200,150,0.15)' : 'rgba(255,107,107,0.15)'
                                            }}>
                                        {change >= 0 ? '+' : ''}{change.toFixed(2)}%
                                    </span>
                                        </div>
                                    );
                                })}
                                {/* 刷新倒计时图标 */}
                                <div
                                    onDoubleClick={() => fetchPrices()}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        background: 'rgba(240,180,41,0.15)',
                                        border: '1px solid rgba(240,180,41,0.3)',
                                        flexShrink: 0,
                                        cursor: 'pointer',
                                        animation: 'pulse 2s ease-in-out infinite',
                                        marginRight: '100px',
                                    }}
                                    title={`下次刷新: ${countdown}秒 (双击刷新)`}
                                >
                            <span style={{
                                fontSize: '12px',
                                fontFamily: 'IBM Plex Mono, monospace',
                                color: '#F0B429',
                            }}>{countdown}</span>
                                </div>
                                {/* 复制一份实现无缝滚动 */}
                                {CRYPTO_LIST.map((crypto) => {
                                    const price = prices[crypto.symbol];
                                    const iconFile = `/coins/${crypto.symbol.toLowerCase()}.webp`;
                                    const isLoading = priceLoading && !price;

                                    if (!price && !priceLoading) {
                                        return (
                                            <div key={`dup-${crypto.symbol}`}
                                                 style={{
                                                     display: 'flex',
                                                     alignItems: 'center',
                                                     gap: '8px',
                                                     flexShrink: 0
                                                 }}>
                                                <img src={iconFile} alt={crypto.symbol} style={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: '50%',
                                                    objectFit: 'cover'
                                                }}/>
                                                <span style={{
                                                    fontSize: '14px',
                                                    fontFamily: 'IBM Plex Mono, monospace',
                                                    color: 'var(--pt-text-secondary)'
                                                }}>--</span>
                                                <span style={{
                                                    fontSize: '12px',
                                                    fontFamily: 'IBM Plex Mono, monospace',
                                                    color: 'var(--pt-text-secondary)',
                                                    padding: '2px 6px',
                                                    borderRadius: '4px',
                                                    background: 'rgba(136,146,164,0.15)'
                                                }}>--</span>
                                            </div>
                                        );
                                    }

                                    if (isLoading || !price) {
                                        return (
                                            <div key={`dup-${crypto.symbol}`}
                                                 style={{
                                                     display: 'flex',
                                                     alignItems: 'center',
                                                     gap: '8px',
                                                     flexShrink: 0
                                                 }}>
                                                <img src={iconFile} alt={crypto.symbol} style={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: '50%',
                                                    objectFit: 'cover'
                                                }}/>
                                                <span style={{
                                                    fontSize: '14px',
                                                    fontFamily: 'IBM Plex Mono, monospace',
                                                    color: 'var(--pt-text-secondary)'
                                                }}>加载中</span>
                                                <span style={{
                                                    fontSize: '12px',
                                                    fontFamily: 'IBM Plex Mono, monospace',
                                                    color: 'var(--pt-text-secondary)',
                                                    padding: '2px 6px',
                                                    borderRadius: '4px',
                                                    background: 'rgba(136,146,164,0.15)'
                                                }}>--</span>
                                            </div>
                                        );
                                    }

                                    const change = Number(price.changePercent24H || 0);
                                    return (
                                        <div key={`dup-${crypto.symbol}`}
                                             style={{display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0}}>
                                            <img src={iconFile} alt={crypto.symbol} style={{
                                                width: 20,
                                                height: 20,
                                                borderRadius: '50%',
                                                objectFit: 'cover'
                                            }}/>
                                            <span style={{
                                                fontSize: '14px',
                                                fontFamily: 'IBM Plex Mono, monospace',
                                                color: 'var(--pt-text-primary)'
                                            }}>${Number(price.lastPrice || 0).toLocaleString()}</span>
                                            <span style={{
                                                fontSize: '12px',
                                                fontFamily: 'IBM Plex Mono, monospace',
                                                color: change >= 0 ? 'var(--pt-bull)' : 'var(--pt-bear)',
                                                padding: '2px 6px',
                                                borderRadius: '4px',
                                                background: change >= 0 ? 'rgba(0,200,150,0.15)' : 'rgba(255,107,107,0.15)'
                                            }}>
                                        {change >= 0 ? '+' : ''}{change.toFixed(2)}%
                                    </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* 系统公告Banner */}
                    {menuMode === 'trading' && (
                        <AnnouncementBanner onAnnouncementClick={handleAnnouncementClick}/>
                    )}

                    <style>{`
                        @keyframes pulse {
                            0%, 100% { opacity: 1; transform: scale(1); }
                            50% { opacity: 0.6; transform: scale(0.95); }
                        }
                        @keyframes sider-handle-breathe {
                            0%, 100% {
                                opacity: 1;
                                transform: translateY(-50%) scale(1);
                            }
                            50% {
                                opacity: 0.55;
                                transform: translateY(-50%) scale(1.05);
                            }
                        }
                        .sider-handle--breathing {
                            animation: sider-handle-breathe 2.2s ease-in-out infinite;
                        }
                        @keyframes marquee {
                            0% { transform: translateX(0); }
                            100% { transform: translateX(-50%); }
                        }
                        @keyframes banner-scroll {
                            0% { transform: translateX(0); }
                            100% { transform: translateX(-50%); }
                        }
                        .price-item {
                            overflow: hidden;
                            white-space: nowrap;
                        }
                        .price-item > div {
                            display: inline-flex !important;
                            animation: marquee 10s linear infinite;
                        }
                        .message-float-btn .ant-float-btn-body {
                            border-radius: 0 !important;
                        }
                        .message-drawer.ant-drawer {
                            width: 25vw !important;
                            height: 50vh !important;
                            right: 0 !important;
                            left: auto !important;
                            top: 48px !important;
                        }
                        .message-drawer .ant-drawer-content-wrapper {
                            width: 25vw !important;
                            height: 50vh !important;
                            border-radius: 0 0 12px 12px !important;
                        }
                        .message-drawer .ant-drawer-content {
                            width: 25vw !important;
                            height: 50vh !important;
                            border-radius: 0 0 12px 12px !important;
                            overflow: hidden !important;
                        }
                        .message-drawer .ant-drawer-body {
                            padding: 12px !important;
                            border-radius: 0 0 12px 12px !important;
                        }
                    `}</style>
                    {/* 面包屑导航已移除 */}
                    {/* <div style={{
                        marginBottom: '16px',
                    }}>
                        <Breadcrumb
                            items={generateBreadcrumb()}
                            style={{margin: '16px 0'}}
                        />
                    </div> */}
                    <div
                        className={isFlushPage ? 'layout-content layout-content--flush' : 'layout-content'}
                    >
                        {children}
                    </div>
                </Content>
            </AntLayout>

            {/* 浮动切换按钮 */}
            <FloatingToggleButton/>

            {/* 浮动消息按钮 - 系统管理页面不显示 */}
            {menuMode !== 'system' && (
                <Tooltip title="" placement="left">
                    <FloatButton
                        className="message-float-btn"
                        icon={<MailOutlined/>}
                        onClick={() => {
                            setMessageModalOpen(true);
                            fetchMessageList(0);
                        }}
                        type="default"
                        style={{
                            right: 0,
                            top: 0,
                            width: 48,
                            height: 48,
                            borderRadius: 0,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                            transition: 'all 0.3s ease-in-out',
                            backgroundColor: messageCount > 0 ? 'rgba(240,180,41,0.2)' : 'rgb(12, 17, 32)',
                            border: 'none',
                        }}
                        badge={messageCount > 0 ? {count: messageCount, overflowCount: 99, offset: [-2, 8]} : undefined}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.1)';
                            e.currentTarget.style.backgroundColor = messageCount > 0 ? 'rgba(240,180,41,0.3)' : 'rgba(24, 144, 255, 0.8)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.backgroundColor = messageCount > 0 ? 'rgba(240,180,41,0.2)' : 'rgb(12, 17, 32)';
                        }}
                    />
                </Tooltip>
            )}

            <ChatFloatWindow/>


            {/* <TestChat /> */}

            {/* 修改密码弹窗 */}
            <Modal
                title="修改密码"
                open={passwordModalOpen}
                onCancel={() => setPasswordModalOpen(false)}
                footer={null}
                destroyOnHidden
            >
                <ChangePasswordForm onSuccess={() => setPasswordModalOpen(false)}/>
            </Modal>

            {/* 消息抽屉 */}
            <Drawer
                open={messageModalOpen}
                onClose={() => {
                    if (messageDetailVisible) return;
                    setMessageModalOpen(false);
                }}
                closable={false}
                footer={null}
                destroyOnHidden
                placement="top 48px"
                style={{
                    position: 'fixed',
                    top: 48,
                    right: 0,
                    width: '25vw',
                    height: '50vh',
                    borderRadius: '0 0 12px 12px',
                }}
                className="message-drawer"
            >
                <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
                    {/* 消息列表区域 - 可滚动 */}
                    <div style={{flex: 1, overflow: 'auto'}}>
                        <Spin spinning={messageLoading}>
                            {messageList.length === 0 ? (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '40px 0',
                                    color: 'var(--pt-text-secondary)'
                                }}>暂无消息</div>
                            ) : (
                                <List
                                    dataSource={messageList}
                                    renderItem={(item: any) => (
                                        <List.Item
                                            style={{
                                                background: item.isRead === 1 ? 'transparent' : 'rgba(0,200,150,0.05)',
                                                borderRadius: '8px',
                                                marginBottom: '8px',
                                                padding: '12px',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <div onClick={() => handleMessageClick(item)} style={{
                                                width: '100%',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start'
                                            }}>
                                                {/* 左侧内容区 */}
                                                <div style={{flex: 1, minWidth: 0}}>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        marginBottom: '4px'
                                                    }}>
                                                        {item.isSystemInfo === 1 && (
                                                            <span style={{
                                                                fontSize: '10px',
                                                                padding: '2px 6px',
                                                                borderRadius: '4px',
                                                                background: 'rgba(139,92,246,0.2)',
                                                                color: '#8B5CF6',
                                                                border: '1px solid rgba(139,92,246,0.3)',
                                                                flexShrink: 0,
                                                            }}>系统</span>
                                                        )}
                                                        <span style={{
                                                            color: item.isRead === 1 ? 'var(--pt-text-secondary)' : 'var(--pt-text-primary)',
                                                            fontSize: '14px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                            {item.content.substring(0, 30) + (item.content.length > 30 ? '...' : '')}
                                                        </span>
                                                    </div>
                                                    <div style={{fontSize: '12px', color: 'var(--pt-text-secondary)'}}>
                                                        {item.createdTime ? new Date(item.createdTime).toLocaleString() : ''}
                                                    </div>
                                                </div>
                                                {/* 右侧状态区 */}
                                                <div style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-end',
                                                    marginLeft: '12px',
                                                    gap: '4px'
                                                }}>
                                                    {/* 未读状态圆点 */}
                                                    <div
                                                        style={{height: '16px', display: 'flex', alignItems: 'center'}}>
                                                        {item.isRead === 0 && (
                                                            <span style={{
                                                                width: '8px',
                                                                height: '8px',
                                                                borderRadius: '50%',
                                                                background: 'var(--pt-bull)',
                                                                display: 'inline-block',
                                                            }}/>
                                                        )}
                                                    </div>
                                                    {/* 删除图标 */}
                                                    <DeleteOutlined
                                                        onClick={(e: React.MouseEvent) => {
                                                            e.stopPropagation();
                                                            handleDeleteMessage(item.id);
                                                        }}
                                                        style={{
                                                            color: 'var(--pt-text-secondary)',
                                                            fontSize: '14px',
                                                            cursor: 'pointer',
                                                            transition: 'color 0.2s',
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.color = 'var(--pt-bear)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.color = 'var(--pt-text-secondary)';
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </List.Item>
                                    )}
                                />
                            )}
                        </Spin>
                    </div>
                    {/* 分页区域 - 固定底部 */}
                    {totalPages >= 1 && (
                        <div style={{
                            borderTop: '1px solid rgba(255,255,255,0.1)',
                            padding: '8px 0',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}>
                            <div style={{transform: 'scale(0.8)', transformOrigin: 'center'}}>
                                <Pagination
                                    current={currentPage + 1}
                                    total={totalPages * 20}
                                    pageSize={20}
                                    onChange={(page) => fetchMessageList(page - 1)}
                                    size="small"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </Drawer>

            {/* 版本更新说明弹窗（用户菜单版本行点击） */}
            <Modal
                title={null == currentVersion ? '版本信息' : `v${currentVersion.version}`}
                open={versionModalOpen}
                onCancel={() => setVersionModalOpen(false)}
                footer={null}
                width={520}
                destroyOnHidden
            >
                {null != currentVersion && (
                    <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
                        <div style={{fontSize: 12, color: 'var(--pt-text-dim)'}}>
                            更新日期 {currentVersion.releaseDate ?? '--'}
                            {currentVersion.summary ? ` · ${currentVersion.summary}` : ''}
                        </div>
                        <div style={{
                            whiteSpace: 'pre-wrap',
                            color: 'var(--pt-text-primary)',
                            fontSize: 13,
                            lineHeight: 1.7,
                            maxHeight: '52vh',
                            overflow: 'auto',
                        }}>
                            {currentVersion.content || '暂无更新说明'}
                        </div>
                    </div>
                )}
            </Modal>

            {/* 消息详情弹窗 */}
            <Modal
                title="消息详情"
                open={messageDetailVisible}
                onCancel={() => setMessageDetailVisible(false)}
                footer={null}
                width={520}
                destroyOnHidden
                maskClosable={false}
                zIndex={1050}
            >
                {expandedContent && expandedContent.startsWith('AI风控')
                    ? renderRiskNotification(expandedContent)
                    : <div style={{
                        whiteSpace: 'pre-wrap',
                        color: 'var(--pt-text-primary)',
                        fontSize: '14px',
                        lineHeight: 1.6
                    }}>{expandedContent}</div>
                }
            </Modal>

            {/* 公告弹窗 */}
            <AnnouncementModal
                visible={announcementModalVisible}
                announcement={selectedAnnouncement}
                onClose={() => {
                    setAnnouncementModalVisible(false);
                    setSelectedAnnouncement(null);
                }}
            />
        </AntLayout>
    );
};

export default Layout;
